import React, { useState, useEffect } from 'react';

interface VideoProxyProps {
  url: string;
  onProxyUrl: (proxyUrl: string) => void;
  onError: (error: string) => void;
}

const VideoProxy: React.FC<VideoProxyProps> = ({ url, onProxyUrl, onError }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (!url) return;
    
    const proxyVideo = async () => {
      setIsLoading(true);
      
      try {
        // Determinar el tipo de URL para aplicar la estrategia correcta
        if (isEmbedUrl(url)) {
          // Para URLs embebidas, creamos un blob URL utilizando un iframe sandbox
          createSandboxedEmbed(url);
        } else if (isDirectVideoUrl(url)) {
          // Para URLs de video directo, intentamos usar un proxy o Blob
          await createProxiedVideo(url);
        } else {
          // URL desconocida, intentar estrategia general
          tryGeneralProxy(url);
        }
      } catch (error) {
        console.error("Error al procesar video mediante proxy:", error);
        onError(`No se pudo procesar el video: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    proxyVideo();
  }, [url]);
  
  // Verificar si es una URL de un iframe embebido
  const isEmbedUrl = (url: string): boolean => {
    return url.includes('embed') || 
           url.includes('player') || 
           url.includes('iframe') ||
           url.includes('fembed') ||
           url.includes('streamtape') ||
           url.includes('mixdrop');
  };
  
  // Verificar si es una URL directa a un video
  const isDirectVideoUrl = (url: string): boolean => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.m3u8', '.mpd'];
    return videoExtensions.some(ext => url.includes(ext));
  };
  
  // Crear un iframe sandboxed para URLs embebidas
  const createSandboxedEmbed = (embedUrl: string) => {
    // Para reproducir dentro de nuestro dominio, usamos blob: como origen
    const blobHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body, html, iframe {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100%;
              border: none;
              overflow: hidden;
            }
          </style>
        </head>
        <body>
          <iframe 
            src="${embedUrl}" 
            width="100%" 
            height="100%" 
            frameborder="0" 
            allowfullscreen="true"
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
          ></iframe>
        </body>
      </html>
    `;
    
    // Crear un blob URL con el HTML
    const blob = new Blob([blobHtml], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    
    // Enviar la URL de blob al componente padre
    onProxyUrl(blobUrl);
    console.log("Creado proxy para iframe embebido:", blobUrl);
  };
  
  // Crear un proxy para videos directos
  const createProxiedVideo = async (videoUrl: string) => {
    try {
      // Si soporta Streams API, usar un proxy service worker
      if ('serviceWorker' in navigator) {
        // Verificar si ya hay un service worker para manejar proxies
        const swRegistration = await navigator.serviceWorker.getRegistration('/videoProxy');
        
        if (!swRegistration) {
          // Normalmente registraríamos un SW aquí, pero por simplicidad usamos un enfoque alternativo
          console.warn("Service Worker no disponible, utilizando enfoque alternativo");
          return tryGeneralProxy(videoUrl);
        }
        
        // Usar mensajería con el SW para establecer el proxy
        const proxyUrl = `/videoProxy?url=${encodeURIComponent(videoUrl)}`;
        onProxyUrl(proxyUrl);
        console.log("Creado proxy para video directo mediante service worker:", proxyUrl);
      } else {
        // Fallback si no hay SW
        tryGeneralProxy(videoUrl);
      }
    } catch (error) {
      console.error("Error al crear proxy para video:", error);
      // Intentar enfoque alternativo
      tryGeneralProxy(videoUrl);
    }
  };
  
  // Estrategia general para cualquier tipo de URL
  const tryGeneralProxy = (sourceUrl: string) => {
    // Dado que no podemos hacer un proxy real sin backend,
    // aquí simplemente pasamos la URL directa y confiamos en que la CSP permita la reproducción
    
    // Modificamos la URL para añadir parámetro de origen si es posible
    let finalUrl;
    try {
      const url = new URL(sourceUrl);
      // Añadir origen si la URL lo permite
      if (url.searchParams) {
        url.searchParams.set('origin', window.location.origin);
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('enablejsapi', '1');
        finalUrl = url.toString();
      } else {
        finalUrl = sourceUrl;
      }
    } catch (e) {
      finalUrl = sourceUrl;
    }
    
    console.log("Usando URL original con modificaciones básicas:", finalUrl);
    onProxyUrl(finalUrl);
  };
  
  // Este componente no renderiza nada visible, solo maneja la lógica de proxy
  return null;
};

export default VideoProxy; 